import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InteractiveChart = ({ chart, onDrillDown }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [chartType, setChartType] = useState(chart.defaultType || 'bar');

  const periods = [
    { value: 'week', label: 'Semana', icon: 'Calendar' },
    { value: 'month', label: 'Mês', icon: 'CalendarRange' },
    { value: 'quarter', label: 'Trimestre', icon: 'CalendarDays' },
    { value: 'year', label: 'Ano', icon: 'CalendarCheck' }
  ];

  const chartTypes = [
    { value: 'bar', label: 'Barras', icon: 'BarChart3' },
    { value: 'line', label: 'Linha', icon: 'TrendingUp' },
    { value: 'pie', label: 'Pizza', icon: 'PieChart' }
  ];

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-foreground mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.dataKey}: {chartType === 'pie' ? entry.value : formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = chart.data.reduce((sum, item) => sum + item.value, 0);
      const percentage = ((data.value / total) * 100).toFixed(1);
      
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-foreground">{data.name}</p>
          <p className="text-sm text-muted-foreground">
            {formatCurrency(data.value)} ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chart.data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="name" 
                stroke="#6b7280"
                fontSize={12}
              />
              <YAxis 
                stroke="#6b7280"
                fontSize={12}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#3b82f6" 
                strokeWidth={3}
                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chart.data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {chart.data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chart.data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="name" 
                stroke="#6b7280"
                fontSize={12}
              />
              <YAxis 
                stroke="#6b7280"
                fontSize={12}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="value" 
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
                cursor="pointer"
                onClick={(data) => onDrillDown && onDrillDown(data)}
              />
            </BarChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <div className="group relative bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all duration-300 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl opacity-20 -mr-16 -mt-16 group-hover:opacity-30 transition-opacity"></div>

      <div className="relative">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Icon name="BarChart3" size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                  {chart.title}
                </h3>
                <p className="text-sm text-muted-foreground">{chart.description}</p>
              </div>
            </div>
          </div>
          
          {/* Controls */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Period Selector */}
            <div className="flex bg-muted/30 rounded-lg p-1">
              {periods.map((period) => (
                <button
                  key={period.value}
                  onClick={() => setSelectedPeriod(period.value)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded transition-all ${
                    selectedPeriod === period.value
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  title={period.label}
                >
                  <Icon name={period.icon} size={14} />
                  <span className="hidden sm:inline">{period.label}</span>
                </button>
              ))}
            </div>

            {/* Chart Type Selector */}
            <div className="flex bg-muted/30 rounded-lg p-1">
              {chartTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setChartType(type.value)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded transition-all ${
                    chartType === type.value
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  title={type.label}
                >
                  <Icon name={type.icon} size={16} />
                  <span className="hidden sm:inline text-xs font-medium">{type.label}</span>
                </button>
              ))}
            </div>

            {/* Export button */}
            <Button
              variant="ghost"
              size="sm"
              iconName="Download"
              iconPosition="left"
              className="text-xs"
            >
              <span className="hidden sm:inline">Exportar</span>
            </Button>
          </div>
        </div>

        {/* Chart Container */}
        <div className="relative bg-muted/10 rounded-lg p-4 border border-border/50">
          {chart.data.length === 0 ? (
            <div className="flex items-center justify-center h-80 text-muted-foreground">
              <div className="text-center">
                <Icon name="BarChart3" size={56} className="mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium mb-1">Sem dados disponíveis</p>
                <p className="text-sm">Nenhum dado para o período selecionado</p>
              </div>
            </div>
          ) : (
            <div className="h-80">
              {renderChart()}
            </div>
          )}
        </div>

        {/* Insights */}
        {chart.insights && chart.insights.length > 0 && (
          <div className="mt-6 border-t border-border pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Lightbulb" size={18} className="text-warning" />
              <h4 className="text-sm font-semibold text-foreground">Insights</h4>
            </div>
            <div className="space-y-2">
              {chart.insights.map((insight, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-warning/5 rounded-lg border border-warning/20">
                  <Icon name="CheckCircle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-foreground leading-relaxed">{insight}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hover effect overlay */}
        <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl pointer-events-none"></div>
      </div>
    </div>
  );
};

export default InteractiveChart;